import { supabase } from './supabaseService';
import type { StorageConnection, StorageConnectionConfig } from '../types/aiPrompts';

interface BackupMetadata {
  timestamp: string;
  tenantId: string | null;
  promptCount: number;
  totalSize: number;
  version: string;
}

interface BackupData {
  metadata: BackupMetadata;
  prompts: any[];
}

class WasabiBackupService {
  async getStorageConnection(tenantId: string | null = null): Promise<StorageConnection | null> {
    let query = supabase
      .from('storage_connections')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (tenantId !== null) {
      query = query.eq('tenant_id', tenantId);
    } else {
      query = query.is('tenant_id', null);
    }

    const { data, error } = await query.limit(1).maybeSingle();

    if (error || !data) {
      console.warn('No active storage connection found');
      return null;
    }

    return this.mapStorageConnection(data);
  }

  async saveStorageConnection(
    serviceType: 'wasabi' | 's3' | 'supabase' | 'local',
    config: StorageConnectionConfig,
    tenantId: string | null = null,
    userId: string
  ): Promise<StorageConnection> {
    // Build query with proper null handling
    let existingQuery = supabase
      .from('storage_connections')
      .select('id')
      .eq('service_type', serviceType);

    if (tenantId === null) {
      existingQuery = existingQuery.is('tenant_id', null);
    } else {
      existingQuery = existingQuery.eq('tenant_id', tenantId);
    }

    const { data: existing } = await existingQuery.maybeSingle();

    if (existing) {
      const { data, error } = await supabase
        .from('storage_connections')
        .update({
          config,
          is_active: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating storage connection:', error);
        throw new Error('Failed to update storage connection');
      }

      return this.mapStorageConnection(data);
    } else {
      const { data, error } = await supabase
        .from('storage_connections')
        .insert({
          service_type: serviceType,
          tenant_id: tenantId,
          config,
          is_active: true,
          created_by: userId
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating storage connection:', error);
        throw new Error('Failed to create storage connection');
      }

      return this.mapStorageConnection(data);
    }
  }

  async testWasabiConnection(config: StorageConnectionConfig): Promise<boolean> {
    try {
      if (!config.endpoint || !config.access_key || !config.secret_key || !config.bucket_name) {
        throw new Error('Missing required Wasabi configuration');
      }

      const timestamp = Date.now();
      const testFileName = `test-connection-${timestamp}.txt`;
      const testContent = 'Connection test successful';

      const success = await this.uploadToWasabi(
        config,
        testFileName,
        testContent,
        'text/plain'
      );

      if (success) {
        await this.deleteFromWasabi(config, testFileName);
      }

      return success;
    } catch (error) {
      console.error('Wasabi connection test failed:', error);
      return false;
    }
  }

  async backupPrompts(tenantId: string | null = null): Promise<{ success: boolean; location: string | null; error?: string }> {
    try {
      console.log(`🔄 Starting prompt backup for tenant: ${tenantId || 'global'}`);

      let query = supabase
        .from('ai_prompts')
        .select('*')
        .eq('is_active', true)
        .order('updated_at', { ascending: false });

      if (tenantId !== null) {
        query = query.eq('tenant_id', tenantId);
      }

      const { data: prompts, error: promptsError } = await query;

      if (promptsError) {
        throw new Error(`Failed to fetch prompts: ${promptsError.message}`);
      }

      const backupData: BackupData = {
        metadata: {
          timestamp: new Date().toISOString(),
          tenantId,
          promptCount: prompts?.length || 0,
          totalSize: JSON.stringify(prompts).length,
          version: '1.0'
        },
        prompts: prompts || []
      };

      const storageConnection = await this.getStorageConnection(tenantId);

      if (storageConnection && storageConnection.service_type === 'wasabi') {
        const wasabiSuccess = await this.backupToWasabi(backupData, storageConnection.config, tenantId);
        if (wasabiSuccess.success) {
          console.log('✅ Backup to Wasabi successful');
          return wasabiSuccess;
        }
        console.warn('⚠️ Wasabi backup failed, falling back to Supabase Storage');
      }

      const supabaseSuccess = await this.backupToSupabaseStorage(backupData, tenantId);
      return supabaseSuccess;

    } catch (error: any) {
      console.error('❌ Backup failed:', error);
      return {
        success: false,
        location: null,
        error: error.message
      };
    }
  }

  private async backupToWasabi(
    backupData: BackupData,
    config: StorageConnectionConfig,
    tenantId: string | null
  ): Promise<{ success: boolean; location: string | null; error?: string }> {
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `prompts-backup-${tenantId || 'global'}-${timestamp}.json`;
      const content = JSON.stringify(backupData, null, 2);

      const success = await this.uploadToWasabi(config, fileName, content, 'application/json');

      if (success) {
        return {
          success: true,
          location: `wasabi://${config.bucket_name}/${fileName}`
        };
      }

      return {
        success: false,
        location: null,
        error: 'Failed to upload to Wasabi'
      };
    } catch (error: any) {
      return {
        success: false,
        location: null,
        error: error.message
      };
    }
  }

  private async backupToSupabaseStorage(
    backupData: BackupData,
    tenantId: string | null
  ): Promise<{ success: boolean; location: string | null; error?: string }> {
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `prompts-backup-${tenantId || 'global'}-${timestamp}.json`;
      const content = JSON.stringify(backupData, null, 2);

      const { data, error } = await supabase.storage
        .from('backups')
        .upload(fileName, content, {
          contentType: 'application/json',
          upsert: false
        });

      if (error) {
        throw error;
      }

      console.log('✅ Backup to Supabase Storage successful');

      return {
        success: true,
        location: `supabase://backups/${fileName}`
      };
    } catch (error: any) {
      console.error('❌ Supabase Storage backup failed:', error);
      return {
        success: false,
        location: null,
        error: error.message
      };
    }
  }

  private async uploadToWasabi(
    config: StorageConnectionConfig,
    fileName: string,
    content: string,
    contentType: string
  ): Promise<boolean> {
    try {
      // Ensure HTTPS endpoint to avoid Mixed Content errors
      let endpoint = config.endpoint!;
      if (endpoint.startsWith('http://')) {
        console.warn('⚠️ Converting HTTP endpoint to HTTPS for security');
        endpoint = endpoint.replace('http://', 'https://');
      }
      if (!endpoint.startsWith('http://') && !endpoint.startsWith('https://')) {
        endpoint = `https://${endpoint}`;
      }

      const url = `${endpoint}/${config.bucket_name}/${fileName}`;

      const date = new Date().toUTCString();
      const amzDate = date;
      const contentMD5 = '';
      const contentLength = new Blob([content]).size.toString();
      const canonicalResource = `/${config.bucket_name}/${fileName}`;

      const canonicalizedAmzHeaders = `x-amz-acl:private\nx-amz-date:${amzDate}\n`;

      const stringToSign = `PUT\n${contentMD5}\n${contentType}\n\n${canonicalizedAmzHeaders}${canonicalResource}`;

      console.log('🔐 Signing details:', {
        method: 'PUT',
        contentType,
        date,
        amzDate,
        canonicalResource,
        canonicalizedAmzHeaders: canonicalizedAmzHeaders.replace(/\n/g, '\\n'),
        stringToSign: stringToSign.replace(/\n/g, '\\n')
      });

      const encoder = new TextEncoder();
      const key = await crypto.subtle.importKey(
        'raw',
        encoder.encode(config.secret_key!),
        { name: 'HMAC', hash: 'SHA-1' },
        false,
        ['sign']
      );

      const signature = await crypto.subtle.sign(
        'HMAC',
        key,
        encoder.encode(stringToSign)
      );

      const signatureBase64 = btoa(String.fromCharCode(...new Uint8Array(signature)));

      const headers = {
        'Content-Type': contentType,
        'Content-Length': contentLength,
        'x-amz-date': amzDate,
        'x-amz-acl': 'private',
        'Authorization': `AWS ${config.access_key}:${signatureBase64}`
      };

      console.log('📤 Request headers:', {
        ...headers,
        Authorization: `AWS ${config.access_key}:${signatureBase64.substring(0, 10)}...`
      });

      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: content
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Wasabi upload failed:', response.status, errorText);
        return false;
      }

      console.log('✅ Wasabi upload successful');
      return true;
    } catch (error) {
      console.error('❌ Error uploading to Wasabi:', error);
      return false;
    }
  }

  private async deleteFromWasabi(
    config: StorageConnectionConfig,
    fileName: string
  ): Promise<boolean> {
    try {
      // Ensure HTTPS endpoint to avoid Mixed Content errors
      let endpoint = config.endpoint!;
      if (endpoint.startsWith('http://')) {
        console.warn('⚠️ Converting HTTP endpoint to HTTPS for security');
        endpoint = endpoint.replace('http://', 'https://');
      }
      if (!endpoint.startsWith('http://') && !endpoint.startsWith('https://')) {
        endpoint = `https://${endpoint}`;
      }

      const url = `${endpoint}/${config.bucket_name}/${fileName}`;

      const date = new Date().toUTCString();
      const amzDate = date;
      const canonicalResource = `/${config.bucket_name}/${fileName}`;
      const canonicalizedAmzHeaders = `x-amz-date:${amzDate}\n`;
      const stringToSign = `DELETE\n\n\n\n${canonicalizedAmzHeaders}${canonicalResource}`;

      console.log('🔐 Delete signing details:', {
        method: 'DELETE',
        date,
        amzDate,
        canonicalResource,
        canonicalizedAmzHeaders: canonicalizedAmzHeaders.replace(/\n/g, '\\n'),
        stringToSign: stringToSign.replace(/\n/g, '\\n')
      });

      const encoder = new TextEncoder();
      const key = await crypto.subtle.importKey(
        'raw',
        encoder.encode(config.secret_key!),
        { name: 'HMAC', hash: 'SHA-1' },
        false,
        ['sign']
      );

      const signature = await crypto.subtle.sign(
        'HMAC',
        key,
        encoder.encode(stringToSign)
      );

      const signatureBase64 = btoa(String.fromCharCode(...new Uint8Array(signature)));

      const headers = {
        'x-amz-date': amzDate,
        'Authorization': `AWS ${config.access_key}:${signatureBase64}`
      };

      console.log('📤 Delete headers:', {
        ...headers,
        Authorization: `AWS ${config.access_key}:${signatureBase64.substring(0, 10)}...`
      });

      const response = await fetch(url, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Wasabi delete failed:', response.status, errorText);
        return false;
      }

      console.log('✅ Wasabi delete successful');
      return true;
    } catch (error) {
      console.error('❌ Error deleting from Wasabi:', error);
      return false;
    }
  }

  async listBackups(tenantId: string | null = null): Promise<Array<{ name: string; size: number; created_at: string }>> {
    try {
      const { data, error } = await supabase.storage
        .from('backups')
        .list('', {
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) {
        console.error('Error listing backups:', error);
        return [];
      }

      const prefix = `prompts-backup-${tenantId || 'global'}`;
      return data
        .filter(file => file.name.startsWith(prefix))
        .map(file => ({
          name: file.name,
          size: file.metadata?.size || 0,
          created_at: file.created_at
        }))
        .slice(0, 10);
    } catch (error) {
      console.error('Error listing backups:', error);
      return [];
    }
  }

  async downloadBackup(fileName: string): Promise<BackupData | null> {
    try {
      const { data, error } = await supabase.storage
        .from('backups')
        .download(fileName);

      if (error || !data) {
        console.error('Error downloading backup:', error);
        return null;
      }

      const text = await data.text();
      return JSON.parse(text);
    } catch (error) {
      console.error('Error parsing backup:', error);
      return null;
    }
  }

  async restoreFromBackup(backupData: BackupData, userId: string): Promise<{ success: boolean; restored: number; errors: string[] }> {
    const errors: string[] = [];
    let restored = 0;

    try {
      for (const prompt of backupData.prompts) {
        try {
          const { error } = await supabase
            .from('ai_prompts')
            .insert({
              scope: prompt.scope,
              module: prompt.module,
              tenant_id: prompt.tenant_id,
              role: prompt.role,
              content: prompt.content,
              version: 1,
              is_active: false,
              change_notes: `Restored from backup: ${backupData.metadata.timestamp}`,
              updated_by: userId
            });

          if (error) {
            errors.push(`Failed to restore prompt ${prompt.id}: ${error.message}`);
          } else {
            restored++;
          }
        } catch (err: any) {
          errors.push(`Error restoring prompt: ${err.message}`);
        }
      }

      return {
        success: errors.length === 0,
        restored,
        errors
      };
    } catch (error: any) {
      return {
        success: false,
        restored,
        errors: [...errors, error.message]
      };
    }
  }

  private mapStorageConnection(data: any): StorageConnection {
    return {
      id: data.id,
      service_type: data.service_type,
      tenant_id: data.tenant_id,
      config: data.config,
      is_active: data.is_active,
      created_by: data.created_by,
      created_at: new Date(data.created_at),
      updated_at: new Date(data.updated_at)
    };
  }
}

export const wasabiBackupService = new WasabiBackupService();
